<script setup>
import { keyBy } from 'lodash-es'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const tech = useTech()
const now = new Date()
const config = useRuntimeConfig()
const route = useRoute()

const bannerDismissed = useCookie('is-bb-banner-dismissed')

const { data } = await useFetch(config.public.BANNER_JSON_URL, {
  query: { affiliate: 'certificates_angular' },
  getCachedData (key) {
    return useNuxtData(key).data.value
  }
})

const { hasPurchased, isGuest } = useUserPurchaseStatus()

const user = useUser()
const { list: certifications } = useCertifications()

const hasPurchasedExamOnly = computed(() => {
  return !certifications?.some(certification => user.accessTo(certification.id, 'TRAINING'))
})

const hasPurchasedAngularJuniorBundlesOnly = computed(() => {
  const certItems = keyBy(certifications, 'slug')

  const juniorAccess = user.accessTo(certItems['angular-junior-developer']?.id, 'TRAINING')
  const isJuniorAccessValid = juniorAccess === 'PURCHASED' || juniorAccess === 'REDEEMED'

  return isJuniorAccessValid &&
    Object.values(certItems).every(cert =>
      cert.slug === 'angular-junior-developer' ||
      (user.accessTo(cert.id, 'TRAINING') !== 'PURCHASED' &&
       user.accessTo(cert.id, 'TRAINING') !== 'REDEEMED')
    )
})

const phase = data.value?.flat()
  .map((item) => ({
    ...item,
    dateString: item.ends,
    remaining: new Date(item.ends) - now,
    header: stylize(item.header),
    title: stylize(item.title),
    prelude: stylize(item.prelude)
  }))
  .find(item => item.remaining > 0)

function stylize (string) {
  return string?.replace('[', '<span class="text-angular-pink">').replace(']', '</span>')
}

const showBanner = useCookie('banner-angular',
  {
    default: () => true,
    watch: 'shallow',
    maxAge: 60 * 60 * 24 * 7,
    SameSite: 'none'
  }
)

const showBannerOnLanding = computed(() => {
  if (route?.path.includes('/angular') && !route?.path?.includes('/blog')) {
    return true
  } else {
    return phase.showOnHomepage || false
  }
})

const currentTheme = computed(() => {
  return phase?.theme || 1
})

const allowBannerOnPage = inject('allowBanners', true)

const { AnalyticsEvent, track } = useAnalytics()
function trackBannerClick () {
  if (!phase?.link) {
    track(AnalyticsEvent.VIEW_PRICING, {
      tech: tech.id,
      via: 'banner'
    })
  }
}
</script>

<template>
  <div class="bg-dark-metal dark:bg-dark-metal/20">
    <BannerRepublic
      v-if="!isGuest && hasPurchased && tech.id === 'angular' && !bannerDismissed && allowBannerOnPage"
      dismissible
      @dismiss="bannerDismissed = true" />
    <div
      v-show="showBanner && (user?.isGuest || !hasPurchased || hasPurchasedExamOnly || hasPurchasedAngularJuniorBundlesOnly) && phase && showBannerOnLanding && allowBannerOnPage "
      class="dark group relative flex h-14 w-full items-center justify-center gap-3 bg-gradient-to-r from-still-red/20 via-purple-dream/20 to-dreem/20 px-2 backdrop-blur-2xl md:h-12 md:gap-5 md:px-6 xl:gap-10"
      :class="{'!from-black !to-black': !route?.path.includes('angular') }">
      <NuxtLink
        :to="phase?.link ? phase?.link : '/angular#pricing'"
        :target="route?.path === '/' || phase?.external ? '_blank' : '_self'"
        class="dark w-full"
        @click="trackBannerClick">
        <div
          v-if="currentTheme === 1"
          class="flex items-center justify-center gap-5 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
          <div
            v-if="phase?.prelude"
            class="flex items-center gap-2 md:flex md:gap-4">
            <div
              class=" w-14 !text-center text-[10px] font-bold uppercase leading-3 text-white md:block md:w-32 md:text-base md:leading-5 xl:w-auto 2xl:text-lg [&>span]:text-angular-pink"
              v-html="phase?.prelude" />
          </div>

          <div class="flex items-center gap-3 md:gap-4 xl:gap-8 2xl:gap-14">
            <div class="flex max-w-[190px] shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap xl:flex-row xl:gap-8">
              <div
                v-if="phase?.title"
                class="text-center text-[11px] !font-semibold leading-none text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem] 2xl:text-lg [&>span]:text-angular-pink"
                v-html="phase?.title" />
              <div
                v-if="phase?.subtitle"
                class="block max-w-fit text-center text-[10px] font-normal text-white md:max-w-full md:text-base xl:inline xl:text-base 2xl:text-lg"
                v-html="phase?.subtitle" />
              <div
                v-if="phase?.interlude"
                class="block text-center text-[10px] font-normal text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-angular-pink"
                v-html="phase?.interlude" />
            </div>
            <div
              v-if="phase?.interlude"
              class="hidden text-center text-[10px] font-normal text-white md:block md:text-base xl:inline xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
              v-html="phase?.interlude" />

            <div
              v-if="phase?.showCountdown"
              class="flex flex-col items-center xl:flex-row xl:gap-4">
              <span
                class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-xs md:tracking-widest">
                {{ phase?.countdownFooter }}
              </span>
              <AppCountdown
                v-bind="{
                  remaining: phase?.remaining,
                  daysToHours: phase?.showDaysAsHours,
                }"
                class="dark"
                box-class="text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-angular-pink/20"
                box-style="" />
            </div>

            <div
              v-if="phase?.addendum"
              class="w-10 text-center text-[9px] font-bold uppercase text-white md:w-auto md:px-0 md:py-1 md:text-base xl:text-base 2xl:text-lg"
              style="letter-spacing: 1.2px">
              {{ phase?.addendum }}
            </div>
          </div>
        </div>
        <div
          v-if="currentTheme === 'bf'"
          class="flex items-center justify-center gap-2 md:w-auto md:gap-5 xl:gap-20">
          <div
            v-if="phase?.prelude"
            class="hidden items-center gap-2 md:flex md:gap-4">
            <img
              :src="
                phase?.icon
                  ? `/images/banner-${phase?.icon}.svg`
                  : '/images/banner-winter.svg'
              "
              class="hidden h-4 w-auto md:h-6"
              alt="logo">
            <div
              class=" hidden !text-center text-[12px] font-black  !leading-none text-white md:inline md:w-[120px]  md:text-base xl:w-auto [&>span]:text-angular-pink"
              v-html="phase?.prelude" />
          </div>

          <div class="flex items-center gap-3 md:gap-5 xl:gap-20">
            <div class="flex w-auto flex-col items-center gap-0.5 md:w-auto xl:flex-row xl:gap-1">
              <div
                v-if="phase?.prelude"
                class="text-center text-[11px] font-black leading-none text-white md:hidden [&>span]:text-angular-pink"
                v-html="phase?.prelude" />
              <div
                v-if="phase?.title"
                class="text-center text-[11px] font-normal !leading-none text-white md:whitespace-nowrap md:text-base xl:text-lg [&>b]:font-bold [&>span]:font-bold [&>span]:text-still-yellow"
                v-html="phase?.title" />
              <div
                v-if="phase?.subtitle"
                class="text-center text-[8px] font-normal !leading-none text-white md:text-sm xl:inline xl:text-base"
                v-html="phase?.subtitle" />
            </div>

            <div
              v-if="phase?.showCountdown"
              class="flex flex-col items-center xl:flex-row xl:gap-4">
              <span
                class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-xs md:tracking-widest">
                {{ phase?.countdownFooter }}
              </span>
              <AppCountdown
                v-bind="{
                  remaining: phase?.remaining,
                  daysToHours: phase?.showDaysAsHours,
                }"
                class="dark"
                box-class="text-white p-0 md:px-2 md:py-1 bg-transparent md:bg-angular-pink/20" />
            </div>

            <div
              v-if="phase?.addendum"
              class="w-10 text-center text-[8px] font-bold uppercase text-white md:w-auto md:px-4 md:py-1 xl:text-xs"
              style="letter-spacing: 1.2px">
              {{ phase?.addendum }}
            </div>
          </div>
        </div>
        <div
          v-if="currentTheme === 'conf'"
          class="flex items-center justify-center gap-2 md:gap-8 xl:gap-10">
          <div
            class="hidden items-center gap-2 md:flex md:gap-4">
            <InlineSvg
              src="/images/icon-fire.svg"
              class="h-4 w-auto text-angular-pink md:h-5"
              alt="fire" />
            <div
              class="w-12 text-center text-xs !font-bold text-angular-pink md:block md:w-auto md:text-sm xl:text-base"
              v-html="phase?.prelude" />
          </div>
          <div class="flex flex-row items-center gap-1 md:gap-3 lg:flex-row lg:gap-5">
            <div class="flex w-[200px] flex-col flex-wrap items-center justify-center gap-x-1 md:w-auto lg:flex-row lg:flex-nowrap lg:gap-8 [&>span]:text-angular-pink">
              <div
                class="hidden text-[10px] font-bold text-angular-pink"
                v-html="phase?.prelude" />
              <div
                v-if="phase?.header"
                class=" items-center gap-4 text-center text-[10px] font-semibold  text-white md:w-auto md:whitespace-nowrap md:text-sm xl:text-base [&>span>span]:text-angular-pink">
                <span v-html="phase.header" />
              </div>
              <div
                v-if="phase?.title"
                class="items-center gap-4 text-center text-[10px] font-semibold leading-none text-white md:w-auto md:whitespace-nowrap md:text-sm md:!leading-[1.2rem] xl:text-base [&>span>span]:text-angular-pink">
                <span v-html="phase.title" />
              </div>
              <NuxtImg
                src="/images/banner-alain.png"
                class="ml-5 hidden h-[36px] rounded-full" />
              <div
                v-if="phase?.subtitle"
                class="text-center text-[10px] font-medium text-white md:text-xs xl:text-base min-[1600px]:block [&>b]:font-bold [&>b]:text-angular-pink [&>span]:!font-semibold [&>span]:text-angular-pink"
                v-html="phase?.subtitle" />
              <div
                v-if="phase?.interlude"
                class="block text-center text-[10px] font-bold text-white md:text-base md:leading-none lg:hidden xl:text-base [&>span]:font-bold [&>span]:text-angular-pink"
                v-html="phase?.interlude" />
            </div>
            <div
              v-if="phase?.interlude"
              class="hidden text-center text-[10px] font-bold text-white md:text-sm lg:block xl:text-base [&>span]:font-bold [&>span]:text-angular-pink lg:[&>span]:ml-2"
              v-html="phase?.interlude" />
            <div class="flex flex-col items-center gap-0 md:flex-row md:gap-2 lg:gap-0">
              <p
                v-if="phase?.preAddendum"
                class="rounded-md text-center text-[8px] font-bold uppercase text-white md:text-[10px] lg:bg-angular-pink/20 lg:px-4 lg:py-1">
                {{ phase?.preAddendum }}
              </p>
              <div
                v-if="phase?.addendum"
                class="w-12 text-center text-[8px] font-bold text-white lg:w-auto  lg:px-4 lg:py-1 lg:text-xs"
                style=""
                v-html="phase?.addendum" />
            </div>
            <div
              v-if="phase?.showCountdown"
              class="flex flex-col items-center md:flex-row md:gap-4">
              <span
                v-if="phase?.id?.includes('EXT')"
                class="whitespace-nowrap text-[8px] font-bold uppercase text-angular-pink md:text-[12px] md:tracking-widest xl:mr-9">
                EXTENDED
              </span>
              <span class="text-gradient-primary whitespace-nowrap text-[8px] font-bold uppercase md:text-[9px] md:tracking-widest">
                {{ phase?.countdownFooter }}
              </span>
              <AppCountdown
                v-bind="{ remaining: phase?.remaining, daysToHours: phase?.showDaysAsHours }"
                class="dark"
                box-class="text-white lg:scale-[1.1] lg:translate-x-2 md:px-2 md:py-1 md:bg-angular-pink/30" />
            </div>
          </div>
        </div>
        <div
          v-if="currentTheme === 'autumn-sales'"
          class="my-auto w-full">
          <div class="mx-auto flex max-w-7xl -translate-x-3 items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
            <div class="flex items-center gap-2">
              <InlineSvg
                src="/images/autumn-leaf.svg"
                class="size-8 text-angular-pink"
                alt="Autumn Sales Banner" />
              <p
                class="w-18 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-angular-pink"
                v-html="phase?.prelude" />
            </div>
            <div class="[&>p]:!leading-1 flex flex-col items-center md:flex-row md:gap-10 lg:gap-16 xl:gap-20">
              <p
                class="text-sm font-normal text-white md:text-lg"
                v-html="phase?.title" />

              <p
                class="text-xs font-bold uppercase tracking-[1.8px] text-angular-pink md:text-lg"
                v-html="phase?.interlude" />
            </div>

            <div
              v-if="phase?.showCountdown"
              class="flex flex-col items-center md:flex-row md:gap-2">
              <span
                v-if="phase?.id?.includes('EXT')"
                class="whitespace-nowrap text-[8px]  font-bold uppercase text-angular-pink md:text-[12px] md:tracking-widest xl:mr-9">
                EXTENDED
              </span>
              <span
                class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
                ends in
              </span>
              <AppCountdown
                v-bind="{
                  remaining: phase?.remaining,
                  daysToHours: phase?.showDaysAsHours,
                }"
                class="dark"
                box-class="text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-angular-pink/20"
                box-style="" />
            </div>
          </div>
        </div>
        <div
          v-if="phase?.theme === 'ang-day'"
          class="flex items-center justify-center gap-5 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
          <div
            v-if="phase?.prelude"
            class="flex items-center gap-2 md:flex md:gap-4">
            <NuxtImg
              src="/images/ang-logo.svg"
              class="h-auto w-6"
              alt="ang-day-logo" />
            <div
              class=" hidden w-12 !text-center text-[10px] font-bold uppercase leading-none text-white md:block md:w-auto md:text-xs lg:text-base [&>span]:text-angular-pink"
              v-html="phase?.prelude" />
          </div>

          <div class="flex items-center gap-3 md:gap-8 xl:gap-8 2xl:gap-14">
            <div class="flex max-w-[180px] shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-row lg:gap-8">
              <div
                v-if="phase?.prelude"
                class="block text-center text-[10px] font-bold uppercase text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-angular-pink"
                v-html="phase?.prelude" />
              <div
                v-if="phase?.title"
                class="text-center text-[11px] font-semibold leading-none text-white md:whitespace-nowrap md:text-sm md:!leading-[1.2rem] lg:text-base xl:text-base xl:leading-[1.2rem] [&>span]:text-angular-pink"
                v-html="phase?.title" />
              <NuxtImg
                src="/images/ang-day-speakers.png"
                class="hidden h-8 w-auto lg:hidden"
                alt="ang-day-speakers" />
              <div
                v-if="phase?.subtitle"
                class="block text-center text-[10px] font-normal text-white md:text-base xl:inline xl:text-base"
                v-html="phase?.subtitle" />
            </div>
            <div
              v-if="phase?.interlude"
              class="hidden text-center text-[10px] font-normal text-white md:block md:text-base xl:inline xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
              v-html="phase?.interlude" />
            <div
              v-if="phase?.cta"
              class="hidden h-[30px] items-center justify-center gap-2 whitespace-nowrap rounded-md bg-angular-pink px-4 text-sm  font-bold text-white group-hover:bg-white group-hover:text-eerie-black md:inline-flex">
              <span>{{ phase?.cta }}</span>
              <span><svg
                width="14"
                height="12"
                viewBox="0 0 14 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6.6 1.5L10.2 5L3.50457e-07 5L5.25303e-07 7L10.2 7L6.6 10.5L8.1 11.9L14 6L8.1 0.100003L6.6 1.5Z"
                  fill="currentColor" />
              </svg></span>
              <span />
            </div>
            <div
              v-if="phase?.showCountdown"
              class="flex flex-col items-center xl:flex-row xl:gap-4">
              <span
                class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:tracking-widest lg:text-xs">
                {{ phase?.countdownFooter }}
              </span>
              <AppCountdown
                v-bind="{
                  remaining: phase?.remaining,
                  daysToHours: phase?.showDaysAsHours,
                }"
                class="dark"
                box-class="text-white p-1 md:px-2 md:py-1 bg-angular-pink/20"
                box-style="" />
            </div>

            <div
              v-if="phase?.addendum"
              class="w-10 text-center text-[10px] font-bold text-angular-pink md:w-auto md:px-4 md:py-1 md:text-base"
              style="letter-spacing: 1.2px">
              {{ phase?.addendum }}
            </div>
          </div>
        </div>
        <div
          v-if="currentTheme === 'autumn-sales'"
          class="my-auto w-full">
          <div class="mx-auto flex max-w-7xl -translate-x-0 items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
            <div class="flex items-center gap-2">
              <InlineSvg
                src="/images/autumn-leaf.svg"
                class="size-6 text-angular-pink md:size-8"
                alt="Autumn Sales Banner" />
              <p
                class="w-18 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-angular-pink"
                v-html="phase?.prelude" />
            </div>
            <div class="[&>p]:!leading-1 flex w-32 flex-col items-center md:w-auto md:flex-row md:gap-10 lg:gap-16 xl:gap-20">
              <p
                class="text-center text-sm font-normal text-white md:text-lg"
                v-html="phase?.title" />

              <p
                v-if="phase?.interlude"
                class="text-xs font-bold uppercase tracking-[1.8px] text-angular-pink md:text-lg"
                v-html="phase?.interlude" />
            </div>

            <div
              v-if="phase?.showCountdown"
              class="flex flex-col items-center md:flex-row md:gap-2">
              <span
                v-if="phase?.id?.includes('EXTENDED')"
                class="whitespace-nowrap text-[8px]  font-bold uppercase text-angular-pink md:text-[12px] md:tracking-widest xl:mr-9">
                EXTENDED
              </span>
              <span
                class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-[10px] md:tracking-widest">
                {{ phase?.countdownFooter }}
              </span>
              <AppCountdown
                v-bind="{
                  remaining: phase?.remaining,
                  daysToHours: phase?.showDaysAsHours,
                }"
                class="dark"
                box-class="text-angular-pink md:text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-angular-pink/20"
                box-style="" />
            </div>
          </div>
        </div>
        <div
          v-if="currentTheme === 'halloween-sale'"
          class="my-auto w-full">
          <div class="mx-auto flex max-w-7xl items-center justify-center gap-3 px-4 md:translate-x-0 md:gap-10 lg:gap-16 xl:gap-20">
            <div class="flex items-center gap-2">
              <InlineSvg
                src="/images/pumpkin.svg"
                class="hidden size-6 sm:block  md:size-6"
                alt="Halloween Sales Banner" />
              <p
                class="w-24 text-sm font-black uppercase leading-none text-white md:w-auto md:text-lg [&>span]:text-[#FF6666]"
                v-html="phase?.prelude" />
            </div>
            <div class="flex w-32 flex-col items-center md:w-auto md:flex-row md:gap-10 lg:gap-16 xl:gap-20 [&>p]:!leading-[90%]">
              <p
                class="text-center text-sm font-normal text-white md:text-lg"
                v-html="phase?.title" />

              <p
                v-if="phase?.interlude"
                class="text-xs font-bold uppercase tracking-[1.8px] text-angular-pink md:text-lg"
                v-html="phase?.interlude" />
            </div>

            <div
              v-if="phase?.showCountdown"
              class="mt-1 flex flex-col items-center md:mt-0 md:flex-row md:gap-2">
              <span
                v-if="phase?.id?.includes('EXTENDED')"
                class="whitespace-nowrap text-[8px]  font-bold uppercase text-angular-pink md:text-[12px] md:tracking-widest xl:mr-9">
                EXTENDED
              </span>
              <span
                class="-mb-1 whitespace-nowrap text-[8px] font-bold uppercase text-white sm:mb-0 md:text-[10px] md:tracking-widest">
                {{ phase?.countdownFooter }}
              </span>
              <AppCountdown
                v-bind="{
                  remaining: phase?.remaining,
                  daysToHours: phase?.showDaysAsHours,
                }"
                class="dark"
                box-class="text-[#FF6666] md:text-white p-1 md:px-2 md:py-1 bg-transparent md:bg-[#FF6666]/20"
                box-style="" />
            </div>
          </div>
        </div>
      </NuxtLink>
      <button
        class="absolute inset-y-0 my-auto size-5 shrink-0 max-[500px]:relative min-[500px]:right-2 xl:right-3"
        @click="showBanner = false">
        <span class="sr-only">close</span><XMarkIcon class="x-5 h-5 text-white" />
      </button>
    </div>
  </div>
</template>
